import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ChevronLeft, ChevronRight, CheckCircle2, Upload, X, Star } from 'lucide-react';

const IS_STATIC = ['heading', 'paragraph', 'divider', 'page_break'];

/**
 * Evaluate a field's `condition` rule against the current values map.
 * Returns true if the field should be visible.
 */
function evalCondition(condition, values) {
  if (!condition || !condition.field_id || !condition.operator) return true;
  const driver = values[condition.field_id];
  const target = condition.value;
  const driverStr = driver == null ? '' : String(driver);
  switch (condition.operator) {
    case 'equals': return driverStr === String(target ?? '');
    case 'not_equals': return driverStr !== String(target ?? '');
    case 'contains': return driverStr.toLowerCase().includes(String(target ?? '').toLowerCase());
    case 'is_empty': return !driverStr;
    case 'is_not_empty': return !!driverStr;
    // Tickbox-specific operators — used by mutually-exclusive pairs.
    case 'is_checked': return driver === true;
    case 'is_not_checked': return driver !== true;
    case 'is_filled': return !!driverStr;
    default: return true;
  }
}

/**
 * Evaluate a field's `disable_when` rule. Same shape as `condition` but
 * inverted intent — when this returns true, render the field disabled.
 */
function evalDisableRule(rule, values) {
  // evalCondition treats "no rule" as "passes", which is what we want
  // for visibility. For disable, "no rule" must mean "not disabled".
  if (!rule || !rule.field_id || !rule.operator) return false;
  return evalCondition(rule, values);
}

/**
 * Renders an online form. Used by:
 *   - the builder preview dialog (`readOnly=true`)
 *   - the candidate completion page (handles real submit)
 *
 * Multi-page: pages are derived from the `field.page` integer (defaults to
 * 1). Conditional visibility uses `field.condition`.
 */
export const FormRenderer = ({
  form,
  readOnly = false,
  initialValues = {},
  onSubmit,
  submitting = false,
  primaryColor = '#f97316',
}) => {
  const [values, setValues] = useState(() => {
    const map = {};
    (initialValues || []).forEach((v) => { map[v.field_id] = v.value; });
    return map;
  });
  const [page, setPage] = useState(1);
  const [errors, setErrors] = useState({});

  // Compute page assignment from the field order — every `page_break`
  // field encountered increments the current page number. This mirrors
  // Jotform's UX where page breaks are placed inline as fields rather
  // than configured per-field via an integer.
  const { fields, pageCount } = useMemo(() => {
    const raw = (form.fields || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    let currentPage = 1;
    const out = [];
    for (const f of raw) {
      if (f.field_type === 'page_break') {
        currentPage += 1;
        continue; // page_break fields don't render in the candidate flow
      }
      out.push({ ...f, page: currentPage });
    }
    return { fields: out, pageCount: Math.max(1, currentPage) };
  }, [form.fields]);

  const visibleFieldsOnPage = useMemo(
    () => fields.filter((f) => (f.page || 1) === page && evalCondition(f.condition, values)),
    [fields, page, values]
  );

  const setValue = useCallback((id, val) => {
    setValues((prev) => ({ ...prev, [id]: val }));
    setErrors((prev) => ({ ...prev, [id]: undefined }));
  }, []);

  const validatePage = () => {
    const errs = {};
    for (const f of visibleFieldsOnPage) {
      if (IS_STATIC.includes(f.field_type)) continue;
      if (!f.required) continue;
      const v = values[f.id];
      let isEmpty = v == null || v === '' || (Array.isArray(v) && v.length === 0);
      // Composite "name" field has {first, last}
      if (f.field_type === 'name') {
        isEmpty = !v || (!v.first?.trim() && !v.last?.trim());
      }
      if (f.field_type === 'rating') {
        isEmpty = !v || Number(v) === 0;
      }
      if (isEmpty) errs[f.id] = 'This field is required';
      if (f.field_type === 'email' && v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        errs[f.id] = 'Invalid email';
      }
      if (f.field_type === 'url' && v && !/^https?:\/\/.+/i.test(v)) {
        errs[f.id] = 'Invalid URL (must start with http:// or https://)';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (!validatePage()) return;
    setPage((p) => Math.min(pageCount, p + 1));
  };

  const handleSubmit = async () => {
    if (!validatePage()) return;
    // Validate ALL pages (skip-validation guard)
    for (const f of fields) {
      if (IS_STATIC.includes(f.field_type)) continue;
      if (!f.required) continue;
      if (!evalCondition(f.condition, values)) continue;
      const v = values[f.id];
      const isEmpty = v == null || v === '' || (Array.isArray(v) && v.length === 0);
      if (isEmpty) {
        // Jump to the offending page
        setPage(f.page || 1);
        setErrors({ [f.id]: 'This field is required' });
        return;
      }
    }
    onSubmit?.(values);
  };

  if (!fields.length) {
    return <p className="text-center text-muted-foreground py-12" data-testid="form-renderer-empty">This form has no fields yet.</p>;
  }

  return (
    <div data-testid="form-renderer">
      {form.description && (
        <p className="text-sm text-muted-foreground mb-6 whitespace-pre-wrap">{form.description}</p>
      )}

      {pageCount > 1 && (
        <div className="flex items-center gap-2 mb-6">
          {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
            <div
              key={p}
              className="flex-1 h-1.5 rounded-full transition-all"
              style={{ backgroundColor: p <= page ? primaryColor : '#e2e8f0' }}
            />
          ))}
          <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
            Page {page} of {pageCount}
          </span>
        </div>
      )}

      <div className="space-y-5">
        {visibleFieldsOnPage.map((f) => (
          <FieldRenderer
            key={f.id}
            field={f}
            value={values[f.id]}
            onChange={(v) => setValue(f.id, v)}
            error={errors[f.id]}
            disabled={readOnly}
          />
        ))}
      </div>

      {!readOnly && (
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
          {page > 1 ? (
            <Button
              variant="outline"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="gap-1.5"
              data-testid="form-renderer-back-btn"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </Button>
          ) : <span />}
          {page < pageCount ? (
            <Button onClick={handleNext} className="gap-1.5" data-testid="form-renderer-next-btn" style={{ backgroundColor: primaryColor }}>
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="gap-1.5" data-testid="form-renderer-submit-btn" style={{ backgroundColor: primaryColor }}>
              <CheckCircle2 className="w-4 h-4" />
              {submitting ? 'Submitting…' : (form.submit_button_label || 'Submit')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

function FieldRenderer({ field, value, onChange, error, disabled }) {
  const baseTestId = `form-field-${field.id}`;
  const errId = `${baseTestId}-error`;

  if (field.field_type === 'heading') {
    return <h3 className="font-heading text-xl text-foreground pt-2" data-testid={baseTestId}>{field.label}</h3>;
  }
  if (field.field_type === 'paragraph') {
    return <p className="text-sm text-muted-foreground whitespace-pre-wrap" data-testid={baseTestId}>{field.label}</p>;
  }
  if (field.field_type === 'divider') {
    return <hr className="border-t border-border my-1" data-testid={baseTestId} />;
  }

  const labelEl = (
    <Label className="text-sm font-medium">
      {field.label}{field.required && <span className="text-rose-500 ml-0.5">*</span>}
    </Label>
  );

  const helpEl = field.help_text && (
    <p className="text-xs text-muted-foreground mt-1">{field.help_text}</p>
  );

  const errEl = error && (
    <p className="text-xs text-rose-600 mt-1" data-testid={errId}>{error}</p>
  );

  switch (field.field_type) {
    case 'textarea':
      return (
        <div data-testid={baseTestId}>
          {labelEl}
          <Textarea
            rows={4}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            className={`mt-1.5 ${error ? 'border-rose-400' : ''}`}
          />
          {helpEl}{errEl}
        </div>
      );
    case 'dropdown':
      return (
        <div data-testid={baseTestId}>
          {labelEl}
          <Select value={value || ''} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger className={`mt-1.5 ${error ? 'border-rose-400' : ''}`}>
              <SelectValue placeholder={field.placeholder || 'Select…'} />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {helpEl}{errEl}
        </div>
      );
    case 'multi_select':
      return (
        <div data-testid={baseTestId}>
          {labelEl}
          <div className="mt-1.5 space-y-1.5">
            {(field.options || []).map((o) => {
              const arr = Array.isArray(value) ? value : [];
              const checked = arr.includes(o.value);
              return (
                <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={(v) => {
                      const set = new Set(arr);
                      if (v) set.add(o.value); else set.delete(o.value);
                      onChange(Array.from(set));
                    }}
                  />
                  <span>{o.label}</span>
                </label>
              );
            })}
          </div>
          {helpEl}{errEl}
        </div>
      );
    case 'radio':
      return (
        <div data-testid={baseTestId}>
          {labelEl}
          <RadioGroup value={value || ''} onValueChange={onChange} disabled={disabled} className="mt-1.5">
            {(field.options || []).map((o) => (
              <div key={o.value} className="flex items-center gap-2">
                <RadioGroupItem value={o.value} id={`${field.id}-${o.value}`} />
                <Label htmlFor={`${field.id}-${o.value}`} className="cursor-pointer">{o.label}</Label>
              </div>
            ))}
          </RadioGroup>
          {helpEl}{errEl}
        </div>
      );
    case 'checkbox':
      return (
        <div data-testid={baseTestId}>
          <label className="flex items-start gap-2 cursor-pointer">
            <Checkbox
              checked={!!value}
              onCheckedChange={(v) => onChange(!!v)}
              disabled={disabled}
              className="mt-0.5"
            />
            <span className="text-sm">
              {field.label}{field.required && <span className="text-rose-500 ml-0.5">*</span>}
            </span>
          </label>
          {helpEl}{errEl}
        </div>
      );
    case 'date':
      return (
        <div data-testid={baseTestId}>
          {labelEl}
          <Input
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={`mt-1.5 ${error ? 'border-rose-400' : ''}`}
          />
          {helpEl}{errEl}
        </div>
      );
    case 'signature':
      return (
        <SignaturePad
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
          error={error}
        />
      );
    case 'file_upload':
      return (
        <div data-testid={baseTestId}>
          {labelEl}
          <div className="mt-1.5">
            <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-colors">
              <Upload className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {value?.filename ? value.filename : 'Click to upload'}
              </span>
              <input
                type="file"
                className="hidden"
                disabled={disabled}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  // For v1 we store the filename only; GridFS upload arrives in v1.1.
                  onChange({ filename: file.name, size: file.size });
                }}
              />
            </label>
          </div>
          {helpEl}{errEl}
        </div>
      );
    case 'number':
      return (
        <div data-testid={baseTestId}>
          {labelEl}
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            className={`mt-1.5 ${error ? 'border-rose-400' : ''}`}
          />
          {helpEl}{errEl}
        </div>
      );
    case 'email':
      return (
        <div data-testid={baseTestId}>
          {labelEl}
          <Input
            type="email"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || 'name@example.com'}
            disabled={disabled}
            className={`mt-1.5 ${error ? 'border-rose-400' : ''}`}
          />
          {helpEl}{errEl}
        </div>
      );
    case 'phone':
      return (
        <div data-testid={baseTestId}>
          {labelEl}
          <Input
            type="tel"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || '+44 7700 900000'}
            disabled={disabled}
            className={`mt-1.5 ${error ? 'border-rose-400' : ''}`}
          />
          {helpEl}{errEl}
        </div>
      );
    case 'url':
      return (
        <div data-testid={baseTestId}>
          {labelEl}
          <Input
            type="url"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || 'https://example.com'}
            disabled={disabled}
            className={`mt-1.5 ${error ? 'border-rose-400' : ''}`}
          />
          {helpEl}{errEl}
        </div>
      );
    case 'time':
      return (
        <div data-testid={baseTestId}>
          {labelEl}
          <Input
            type="time"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={`mt-1.5 ${error ? 'border-rose-400' : ''}`}
          />
          {helpEl}{errEl}
        </div>
      );
    case 'name': {
      const v = value && typeof value === 'object' ? value : { first: '', last: '' };
      return (
        <div data-testid={baseTestId}>
          {labelEl}
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            <div>
              <Input
                placeholder="First name"
                value={v.first || ''}
                onChange={(e) => onChange({ ...v, first: e.target.value })}
                disabled={disabled}
                className={error ? 'border-rose-400' : ''}
              />
              <p className="text-xs text-muted-foreground mt-0.5">First name</p>
            </div>
            <div>
              <Input
                placeholder="Last name"
                value={v.last || ''}
                onChange={(e) => onChange({ ...v, last: e.target.value })}
                disabled={disabled}
                className={error ? 'border-rose-400' : ''}
              />
              <p className="text-xs text-muted-foreground mt-0.5">Last name</p>
            </div>
          </div>
          {helpEl}{errEl}
        </div>
      );
    }
    case 'rating': {
      const max = 5;
      const current = Number(value) || 0;
      return (
        <div data-testid={baseTestId}>
          {labelEl}
          <div className="flex items-center gap-1 mt-1.5">
            {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                disabled={disabled}
                onClick={() => onChange(n)}
                className="p-0.5 transition-transform hover:scale-110"
                data-testid={`${baseTestId}-star-${n}`}
              >
                <Star
                  className="w-7 h-7"
                  fill={n <= current ? '#f59e0b' : 'transparent'}
                  stroke={n <= current ? '#f59e0b' : '#cbd5e1'}
                  strokeWidth={1.5}
                />
              </button>
            ))}
            {current > 0 && (
              <span className="text-sm text-muted-foreground ml-2">{current}/5</span>
            )}
          </div>
          {helpEl}{errEl}
        </div>
      );
    }
    case 'text':
    default:
      return (
        <div data-testid={baseTestId}>
          {labelEl}
          <Input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            className={`mt-1.5 ${error ? 'border-rose-400' : ''}`}
          />
          {helpEl}{errEl}
        </div>
      );
  }
}

function SignaturePad({ field, value, onChange, disabled, error }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    // Restore existing signature
    if (value && typeof value === 'string' && value.startsWith('data:image')) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = value;
    }
  }, [value]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawingRef.current = true;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    onChange(null);
  };

  return (
    <div data-testid={`form-field-${field.id}`}>
      <Label className="text-sm font-medium">
        {field.label}{field.required && <span className="text-rose-500 ml-0.5">*</span>}
      </Label>
      <div className={`relative mt-1.5 border rounded-lg bg-white ${error ? 'border-rose-400' : 'border-border'}`}>
        <canvas
          ref={canvasRef}
          width={500}
          height={140}
          className="w-full h-[140px] touch-none rounded-lg cursor-crosshair"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
        {value && (
          <button
            type="button"
            onClick={clear}
            className="absolute top-2 right-2 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>
      {field.help_text && <p className="text-xs text-muted-foreground mt-1">{field.help_text}</p>}
      {error && <p className="text-xs text-rose-600 mt-1">{error}</p>}
    </div>
  );
}
